import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { INCIDENT_SEVERITIES, INCIDENT_TYPES, SEVERITY_LABELS, type FollowUpAction } from "@server/incidents/schema";
import type { IncidentView } from "@server/incidents/types";
import { attachmentNames } from "@server/uploads/files";
import { ApiError, useIncident, useIncidentFollowUp, useMe, useRetryIncident } from "../api/client";
import { Button, ErrorBanner, Select } from "../components/ui";
import { Workspace } from "../components/Workspace";

// The server's reasons when it gives them (a 400 from the checks), else its message.
const messageOf = (error: unknown): string | undefined =>
  error instanceof ApiError ? (error.issues.length > 0 ? [...new Set(error.issues)].join(". ") : error.message) : error ? "Something went wrong. Please try again." : undefined;

const timeOf = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const dayOf = (iso: string): string => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

// The steps in the order the job runs them; each ticks as the job saves it.
const STEPS: { key: keyof IncidentView["done"]; label: string }[] = [
  { key: "channel", label: "Creating a dedicated incident channel" },
  { key: "invite", label: "Adding you and the responders to the new channel" },
  { key: "post", label: "Posting to #incident and notifying the responders" },
  { key: "jira", label: "Creating a Jira ticket in the Incidents space" },
  { key: "links", label: "Linking the ticket and the follow-up page" },
];

function Creating({ view }: { view: IncidentView }) {
  const active = STEPS.findIndex((step) => !view.done[step.key]);
  const current = active === -1 ? STEPS.length - 1 : active;
  const names = view.responders.map((responder) => responder.name);
  return (
    <div className="phase-triage">
      <div className="phase-head">
        <h1>Creating your incident…</h1>
        <p>{names.length > 0 ? `${names.join(", ")} are notified as soon as this finishes.` : "The responders are notified as soon as this finishes."}</p>
      </div>
      <div className="triage-visual">
        <div className="triage-bar">
          <div className="triage-bar-fill" style={{ width: `${Math.round(((current + 0.5) / STEPS.length) * 100)}%` }} />
        </div>
      </div>
      <ul className="checklist">
        {STEPS.map((step, index) => (
          <li key={step.key} className={view.done[step.key] ? "done" : index === current ? "active" : ""}>
            <span className="tick">✓</span>
            {step.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SeverityBadge({ view }: { view: IncidentView }) {
  return <span className={`severity-pill severity-${view.form.severity}`}>{SEVERITY_LABELS[view.form.severity]}</span>;
}

function Chips({ items }: { items: readonly string[] }) {
  return (
    <span className="cat-chips">
      {items.map((item) => (
        <span key={item} className="cat-chip">
          {item}
        </span>
      ))}
    </span>
  );
}

function ExternalLink({ url, children }: { url: string; children?: string }) {
  return (
    <a href={url} className="slack-link" target="_blank" rel="noreferrer">
      {children ?? url}
    </a>
  );
}

const ROLE_SUFFIX = { owner: " (owner)", backup: " (backup owner)", notify: "" } as const;

const HEADINGS = { INCIDENT_CREATED: "Incident created", INCIDENT_RESOLVED: "Incident resolved", INCIDENT_COMPLETE: "Incident closed out" } as const;

// Who did an item and when, for the line under it.
const doneBy = (item: { at: string; by: string }): string => `${item.by ? `${item.by}, ` : ""}${dayOf(item.at)}`;

function ConfirmType({ view, busy, onConfirm }: { view: IncidentView; busy: boolean; onConfirm: (action: FollowUpAction) => void }) {
  const confirmed = view.followUp.confirmed;
  const [types, setTypes] = useState<string[]>(() => [...(confirmed?.types ?? view.form.types)]);
  const [severity, setSeverity] = useState<string>(() => SEVERITY_LABELS[confirmed?.severity ?? view.form.severity]);
  const toggle = (type: string): void => setTypes((current) => (current.includes(type) ? current.filter((item) => item !== type) : [...current, type]));
  const severityKey = INCIDENT_SEVERITIES.find((key) => SEVERITY_LABELS[key] === severity);
  return (
    <div className="follow-up-confirm">
      <div className="check-group" role="group" aria-label="Type of incident">
        {INCIDENT_TYPES.map((type) => (
          <label key={type} className="check-chip">
            <input type="checkbox" checked={types.includes(type)} onChange={() => toggle(type)} />
            <span className="check-box">✓</span>
            {type}
          </label>
        ))}
      </div>
      <div className="follow-up-severity">
        <Select id="confirmed-severity" required aria-label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)} options={INCIDENT_SEVERITIES.map((key) => SEVERITY_LABELS[key])} />
        <Button
          variant="secondary"
          disabled={busy || types.length === 0 || !severityKey}
          onClick={() => severityKey && onConfirm({ action: "confirm", types: INCIDENT_TYPES.filter((type) => types.includes(type)), severity: severityKey })}
        >
          {confirmed ? "Update" : "Confirm"}
        </Button>
      </div>
    </div>
  );
}

const isHttps = (url: string | undefined): url is string => Boolean(url?.startsWith("https://"));

// Where the write-up lives (a Google Doc). The owner can add, change or remove the link; everyone sees it once it is set.
function WriteUpLink({ url, busy, canEdit, onSave }: { url?: string; busy: boolean; canEdit: boolean; onSave: (url: string) => void }) {
  const [value, setValue] = useState(url ?? "");
  const changed = value.trim() !== (url ?? "");
  return (
    <div className="follow-up-link">
      {canEdit && (
        <form
          className="follow-up-link-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(value.trim());
          }}
        >
          <input
            type="text"
            inputMode="url"
            aria-label="Link to the write-up"
            placeholder="Link to the write-up, for example a Google Doc (optional)"
            maxLength={500}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <Button type="submit" variant="secondary" disabled={busy || !changed}>
            {url && value.trim() === "" ? "Remove link" : "Save link"}
          </Button>
        </form>
      )}
      {isHttps(url) && (
        <a className="slack-link" href={url} target="_blank" rel="noreferrer">
          Open the write-up ↗
        </a>
      )}
    </div>
  );
}

function TickItem({
  label,
  item,
  enabled,
  resolved,
  onChange,
  children,
}: {
  label: string;
  item?: { at: string; by: string };
  enabled: boolean;
  resolved: boolean;
  onChange: (done: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <li className="follow-up-item">
      <div className="follow-up-item-head">
        <label className="check-chip">
          <input type="checkbox" checked={Boolean(item)} disabled={!enabled} onChange={(e) => onChange(e.target.checked)} />
          <span className="check-box">✓</span>
          {label}
        </label>
        <span className="follow-up-meta">{item ? `Done by ${doneBy(item)}` : resolved ? "" : "Available once the incident is resolved"}</span>
      </div>
      {children}
    </li>
  );
}

// Where the owner resolves the incident and finishes what the process asks for afterwards.
function FollowUp({ view }: { view: IncidentView }) {
  const update = useIncidentFollowUp();
  const { followUp, canManage } = view;
  const resolved = Boolean(followUp.resolvedAt);
  const busy = update.isPending;
  const run = (action: FollowUpAction): void => update.mutate({ id: view.id, action });
  const done = [followUp.confirmed, followUp.writeUp, followUp.lessons].filter(Boolean).length;
  return (
    <section className="follow-up" aria-labelledby="follow-up-title">
      <div className="follow-up-head">
        <div>
          <h2 id="follow-up-title">Follow-up</h2>
          <span className="follow-up-count">{done} of 3 done</span>
        </div>
        {canManage &&
          (resolved ? (
            <Button variant="secondary" disabled={busy} onClick={() => run({ action: "reopen" })}>
              Reopen
            </Button>
          ) : (
            <Button disabled={busy} onClick={() => run({ action: "resolve" })}>
              Mark resolved
            </Button>
          ))}
      </div>
      <p className="follow-up-status">
        {resolved && followUp.resolvedAt ? (
          <>
            <strong>Resolved</strong> on {dayOf(followUp.resolvedAt)}
            {followUp.resolvedBy ? ` by ${followUp.resolvedBy}` : ""}.{" "}
            {view.status === "INCIDENT_COMPLETE"
              ? "Everything below is done."
              : `The owner is messaged on Slack on ${dayOf(followUp.dueAt ?? followUp.resolvedAt)} if anything below is still open.`}
          </>
        ) : (
          <>
            <strong>Open.</strong> The owner marks it resolved once the fix is confirmed and the impact has stopped, which posts a note in the incident channel.
          </>
        )}
        {!canManage && " Only the owner, the backup owner or an admin can change this."}
      </p>
      {update.error && <ErrorBanner>{messageOf(update.error)}</ErrorBanner>}

      <ul className="follow-up-items">
        <li className="follow-up-item">
          <div className="follow-up-item-head">
            <span className="follow-up-item-title">Type and severity confirmed</span>
            <span className="follow-up-meta">{followUp.confirmed ? `Confirmed by ${doneBy(followUp.confirmed)}` : "Not confirmed yet. What is above is the raiser's best guess."}</span>
          </div>
          {canManage ? (
            <ConfirmType key={`${followUp.confirmed?.at ?? "new"}`} view={view} busy={busy} onConfirm={run} />
          ) : (
            followUp.confirmed && <p className="record-note">{`${followUp.confirmed.types.join(", ")} — ${SEVERITY_LABELS[followUp.confirmed.severity]}`}</p>
          )}
        </li>
        <TickItem label="Report write-up completed" item={followUp.writeUp} enabled={canManage && resolved && !busy} resolved={resolved} onChange={(value) => run({ action: "writeUp", done: value })}>
          {(resolved || followUp.writeUpUrl) && (
            <WriteUpLink key={followUp.writeUpUrl ?? "none"} url={followUp.writeUpUrl} busy={busy} canEdit={canManage && resolved} onSave={(url) => run({ action: "writeUpLink", url })} />
          )}
        </TickItem>
        <TickItem label="Lessons learnt shared" item={followUp.lessons} enabled={canManage && resolved && !busy} resolved={resolved} onChange={(value) => run({ action: "lessons", done: value })} />
      </ul>
    </section>
  );
}

function Created({ view }: { view: IncidentView }) {
  const navigate = useNavigate();
  const { form, links } = view;
  const me = useMe();
  const isRaiser = me.data?.email?.toLowerCase() === view.creatorEmail.toLowerCase();
  const raiserName = isRaiser ? "You" : view.creatorName;
  const owner = view.responders.find((responder) => responder.role === "owner");
  const channel = links.channelName ? `#${links.channelName}` : "the new channel";
  const names = view.responders.map((responder) => `@${responder.name}`).join(", ");
  const fileNames = form.files?.length ? attachmentNames(form.files) : [];
  const resolved = view.status === "INCIDENT_RESOLVED" || view.status === "INCIDENT_COMPLETE";
  const heading = view.status === "INCIDENT_RESOLVED" ? HEADINGS.INCIDENT_RESOLVED : view.status === "INCIDENT_COMPLETE" ? HEADINGS.INCIDENT_COMPLETE : HEADINGS.INCIDENT_CREATED;

  return (
    <div className="result result-incident">
      <div className={`result-badge ${resolved ? "result-badge-good" : "result-badge-incident"}`}>
        {resolved ? "✓" : "🚨"} {heading}
      </div>
      <h1>{heading}</h1>
      <p>
        This has been posted to <strong>#incident</strong>, a dedicated channel has been created for it, and a Jira ticket has been opened in the Incidents space. All further comms
        about this incident should happen in <strong>{channel}</strong> from now on — that's where everything gets documented going forward.{" "}
        {owner && <strong>{owner.name} takes ownership of the incident from here</strong>}
        {owner && (isRaiser ? ", so your part is done once the form is submitted." : ".")}
      </p>

      <FollowUp view={view} />

      <div className="handoff">
        <div className="slack-preview">
          <div className="slack-preview-head">
            <span className="slack-avatar">🚨</span>
            <div>
              <span className="slack-bot-name">
                Report an app incident<span className="slack-app-badge">APP</span>
              </span>
              <span className="slack-time">
                Today at {timeOf(view.createdAt)} · #incident
                {links.postUrl && (
                  <>
                    {" · "}
                    <a href={links.postUrl} target="_blank" rel="noreferrer">
                      open in Slack
                    </a>
                  </>
                )}
              </span>
            </div>
          </div>
          {names && (
            <p className="slack-line">
              🔔 <strong>Notifying in the first instance:</strong> {names}
            </p>
          )}
          {links.channelUrl && (
            <p className="slack-line">
              <strong>DEDICATED CHANNEL:</strong> <ExternalLink url={links.channelUrl} />
            </p>
          )}
          <dl className="slack-fields">
            <div>
              <dt>Title</dt>
              <dd>{form.title}</dd>
            </div>
            <div>
              <dt>Companies impacted</dt>
              <dd>{form.companiesImpacted}</dd>
            </div>
            <div>
              <dt>Severity</dt>
              <dd>
                <SeverityBadge view={view} />
              </dd>
            </div>
          </dl>
          <p className="slack-detail-label">Type (as raised — owner to confirm)</p>
          <Chips items={form.types} />
          <p className="slack-detail-label">Description</p>
          <p className="slack-detail">{form.description}</p>
          {fileNames.length > 0 && (
            <ul className="file-chips">
              {fileNames.map((name) => (
                <li key={name} className="file-chip">
                  <span>📎 {name}</span>
                </li>
              ))}
            </ul>
          )}

          {links.jiraUrl && links.jiraKey && (
            <>
              <p className="slack-line" style={{ marginTop: 10 }}>
                <strong>JIRA TICKET:</strong> <ExternalLink url={links.jiraUrl} />
              </p>
              <div className="list-unfurl">
                <div className="list-unfurl-head">🎫 Jira · Incidents space · {links.jiraKey}</div>
                <p className="list-unfurl-title">{form.title}</p>
                <div className="list-field-row">
                  <span className="list-field-label">Issue type</span>
                  <Chips items={["Incident"]} />
                </div>
                <div className="list-field-row">
                  <span className="list-field-label">Severity</span>
                  <SeverityBadge view={view} />
                </div>
                <div className="list-field-row">
                  <span className="list-field-label">Status</span>
                  <Chips items={["Open"]} />
                </div>
                <div className="list-field-row">
                  <span className="list-field-label">Assignee</span>
                  <span>{view.assigned && owner ? `${owner.name} (owner)` : "Unassigned"}</span>
                </div>
                <div className="list-field-row">
                  <span className="list-field-label">Raised by</span>
                  <span>{view.creatorName}</span>
                </div>
                <div className="list-field-row">
                  <span className="list-field-label">Linked</span>
                  <span>
                    {channel}
                    {" · Follow-up page"}
                  </span>
                </div>
                <p className="list-unfurl-note">Created automatically in the Incidents space, with the details from this form copied across and the Slack channel linked back.</p>
              </div>
            </>
          )}

          <p className="slack-line" style={{ marginTop: 10 }}>
            <strong>POST-INCIDENT FOLLOW-UP:</strong> the Follow-up section on this page
          </p>
          <p className="slack-line" style={{ marginTop: 10 }}>
            📌 All future updates on this incident should happen in that channel — keeping the link here means it's easy to find again even once the channel is archived.
          </p>
        </div>

        <div className="channel-preview">
          <div className="channel-preview-head">
            <span className="channel-hash">#</span>
            <strong>{links.channelName ?? "incident"}</strong>
            <span className="tracker-sub">New channel</span>
          </div>
          <p className="channel-topic">Topic: {form.title} — all updates on this incident live here from now on.</p>
          {links.jiraKey && (
            <p className="channel-topic">
              📌 Pinned: Jira ticket <strong>{links.jiraKey}</strong> and the follow-up page.
            </p>
          )}
          <div className="channel-members">
            <span className="channel-member-chip">
              <span className="channel-member-avatar">{isRaiser ? "🙋" : raiserName.charAt(0).toUpperCase()}</span>
              {raiserName} (raiser)
            </span>
            {view.responders.map((responder) => (
              <span key={responder.name} className="channel-member-chip">
                <span className="channel-member-avatar">{responder.name.charAt(0).toUpperCase()}</span>
                {responder.name}
                {ROLE_SUFFIX[responder.role]}
              </span>
            ))}
          </div>
          <p className="channel-callout">
            ✓ {raiserName}
            {view.responders.length > 0 ? `, ${view.responders.map((responder) => responder.name).join(", ")}` : ""} were added automatically — nobody needs inviting.
          </p>
        </div>
      </div>

      <div className="result-actions">
        <Button onClick={() => void navigate("/incidents")}>Close</Button>
        <Button variant="secondary" onClick={() => void navigate("/new/incident")}>
          Report another incident
        </Button>
      </div>
    </div>
  );
}

function Failed({ view }: { view: IncidentView }) {
  const retry = useRetryIncident();
  return (
    <div className="result">
      <div className="result-badge result-badge-incident">Incident not fully created</div>
      <h1>{view.form.title}</h1>
      <p>
        Something went wrong, and engineering has been alerted. The responders may already have been told. If this is urgent, post in <strong>#incident</strong> now.
      </p>
      {view.lastError && <p className="mono-note">{view.lastError}</p>}
      {retry.error && <ErrorBanner>{messageOf(retry.error)}</ErrorBanner>}
      <div className="result-actions">
        <Button disabled={retry.isPending} onClick={() => retry.mutate(view.id)}>
          Try again
        </Button>
        <Link to="/new/eng" className="btn btn-secondary">
          Raise an engineering ticket
        </Link>
      </div>
    </div>
  );
}

export function IncidentDetail() {
  const { id = "" } = useParams();
  const query = useIncident(id);
  const view = query.data;

  if (query.isError) {
    return (
      <Workspace step="result" flow="incident">
        <ErrorBanner>{messageOf(query.error) ?? "We couldn't load this incident."}</ErrorBanner>
      </Workspace>
    );
  }
  if (!view) {
    return <Workspace step="result" flow="incident">{null}</Workspace>;
  }
  const creating = view.status === "INCIDENT_QUEUED";
  return (
    <Workspace step={creating ? "triage" : "result"} flow="incident">
      {creating && <Creating view={view} />}
      {view.status === "INCIDENT_FAILED" && <Failed view={view} />}
      {(view.status === "INCIDENT_CREATED" || view.status === "INCIDENT_RESOLVED" || view.status === "INCIDENT_COMPLETE") && <Created view={view} />}
    </Workspace>
  );
}
